import type { IChargeRepository, PaginatedResponse } from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type {
    Charge,
    ChargeQueryParams,
    ChargeStatus,
    ChargeSummary,
    ChargeType,
    CreateChargeDTO,
    GenerateBatchChargesDTO,
    MemberChargeSummary,
    UpdateChargeDTO,
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

// API Response Types
interface ApiChargeResponse {
  id: string;
  memberId: string;
  type: ChargeType;
  concept: string;
  amount: number;
  balance: number;
  period: string | null;
  dueDate: string;
  status: ChargeStatus;
  reference: string | null;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  isOverdue: boolean;
  daysOverdue: number;
  memberName?: string;
  unitId?: string;
  unitName?: string;
}

interface ApiChargeSummaryResponse {
  totalPending: number;
  totalPartial: number;
  totalPaid: number;
  amountPending: number;
  amountPaid: number;
}

interface ApiMemberChargeSummaryResponse {
  memberId: string;
  memberName: string;
  totalOwed: number;
  pendingAmount: number;
  overdueAmount: number;
  pendingCount: number;
  overdueCount: number;
  nextDueDate: string | null;
}

// Mapper
const mapApiChargeToCharge = (apiCharge: ApiChargeResponse): Charge => ({
  id: apiCharge.id,
  memberId: apiCharge.memberId,
  type: apiCharge.type,
  concept: apiCharge.concept,
  amount: apiCharge.amount,
  balance: apiCharge.balance,
  period: apiCharge.period,
  dueDate: apiCharge.dueDate,
  status: apiCharge.status,
  reference: apiCharge.reference,
  notes: apiCharge.notes,
  createdBy: apiCharge.createdBy,
  createdAt: apiCharge.createdAt,
  updatedAt: apiCharge.updatedAt,
  isOverdue: apiCharge.isOverdue,
  daysOverdue: apiCharge.daysOverdue,
  memberName: apiCharge.memberName,
  unitId: apiCharge.unitId,
  unitName: apiCharge.unitName,
});

export class ApiChargeRepository implements IChargeRepository {
  // === Admin Queries ===

  async getAllCharges(params?: ChargeQueryParams): Promise<PaginatedResponse<Charge>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      type: params?.type,
      status: params?.status,
      period: params?.period,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder?.toLowerCase() as 'asc' | 'desc' | undefined,
      unitId: params?.unitId,
    });
    const response = await client.get<PaginatedResponse<ApiChargeResponse>>(
      `v1/charges?${queryString}`
    );
    return {
      ...response,
      data: response.data.map(mapApiChargeToCharge),
    };
  }

  async getSummary(): Promise<ChargeSummary> {
    const client = getApiClient();
    return client.get<ApiChargeSummaryResponse>('v1/charges/summary');
  }

  async getOverdueCharges(): Promise<Charge[]> {
    const client = getApiClient();
    const response = await client.get<ApiChargeResponse[]>('v1/charges/overdue');
    return response.map(mapApiChargeToCharge);
  }

  async getChargeById(id: string): Promise<Charge | null> {
    const client = getApiClient();
    try {
      const apiCharge = await client.get<ApiChargeResponse>(`v1/charges/${id}`);
      return mapApiChargeToCharge(apiCharge);
    } catch {
      return null;
    }
  }

  async getMemberCharges(memberId: string, params?: ChargeQueryParams): Promise<PaginatedResponse<Charge>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      type: params?.type,
      status: params?.status,
      period: params?.period,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder?.toLowerCase() as 'asc' | 'desc' | undefined,
    });
    const response = await client.get<PaginatedResponse<ApiChargeResponse>>(
      `v1/charges/member/${memberId}?${queryString}`
    );
    return {
      ...response,
      data: response.data.map(mapApiChargeToCharge),
    };
  }

  async getMemberSummary(memberId: string): Promise<MemberChargeSummary> {
    const client = getApiClient();
    return client.get<ApiMemberChargeSummaryResponse>(`v1/charges/member/${memberId}/summary`);
  }

  // === Resident Queries ===

  async getMyCharges(unitId?: string): Promise<Charge[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<ApiChargeResponse>>(
      `v1/charges/my${unitId ? `?unitId=${encodeURIComponent(unitId)}` : ''}`
    );
    return response.data.map(mapApiChargeToCharge);
  }

  async getMyChargesAll(params?: ChargeQueryParams): Promise<PaginatedResponse<Charge>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      type: params?.type,
      status: params?.status,
      period: params?.period,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder?.toLowerCase() as 'asc' | 'desc' | undefined,
      unitId: params?.unitId,
    });
    const response = await client.get<PaginatedResponse<ApiChargeResponse>>(
      `v1/charges/my/all?${queryString}`
    );
    return {
      ...response,
      data: response.data.map(mapApiChargeToCharge),
    };
  }

  async getMySummary(unitId?: string): Promise<MemberChargeSummary> {
    const client = getApiClient();
    return client.get<ApiMemberChargeSummaryResponse>(
      `v1/charges/my/summary${unitId ? `?unitId=${encodeURIComponent(unitId)}` : ''}`
    );
  }

  // === Commands ===

  async createCharge(data: CreateChargeDTO): Promise<Charge> {
    const client = getApiClient();
    const apiCharge = await client.post<ApiChargeResponse>('v1/charges', data);
    return mapApiChargeToCharge(apiCharge);
  }

  async generateBatchCharges(data: GenerateBatchChargesDTO): Promise<Charge[]> {
    const client = getApiClient();
    const response = await client.post<ApiChargeResponse[]>('v1/charges/generate', data);
    return response.map(mapApiChargeToCharge);
  }

  async updateCharge(id: string, data: UpdateChargeDTO): Promise<Charge> {
    const client = getApiClient();
    const apiCharge = await client.put<ApiChargeResponse>(`v1/charges/${id}`, data);
    return mapApiChargeToCharge(apiCharge);
  }

  async cancelCharge(id: string): Promise<Charge> {
    const client = getApiClient();
    const apiCharge = await client.post<ApiChargeResponse>(`v1/charges/${id}/cancel`);
    return mapApiChargeToCharge(apiCharge);
  }

  async deleteCharge(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/charges/${id}`);
  }
}
